'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import PostCard from './PostCard';
import FilterControls from './FilterControls';
import PostSkeleton from './PostSkeleton';
import EmptyState from './EmptyState';
import ToastNotification from './ToastNotification';
import BottomTabNavigation from '@/components/common/BottomTabNavigation';
import FloatingActionButton from '@/components/common/FloatingActionButton';
import { supabase } from '@/lib/supabase';
import { sdk } from '@farcaster/frame-sdk';

interface Post {
  id: string;
  content: string;
  projectName: string;
  projectImage: string;
  projectImageAlt: string;
  timestamp: string;
  likes: number;
  shares: number;
  status: 'pending' | 'approved' | 'rejected';
  platform: 'X' | 'Farcaster';
  projectId: string;
}

interface Project {
  id: string;
  name: string;
}

interface Toast {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  isVisible: boolean;
}

interface UserStats {
  fid: number | null;
  username: string;
  followerCount: number;
  followingCount: number;
}

const DashboardInteractive = () => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [toast, setToast] = useState<Toast>({ message: '', type: 'info', isVisible: false });
  const [showFilters, setShowFilters] = useState(false);


  const [userStats, setUserStats] = useState<UserStats>({
    fid: null,
    username: 'Guest',
    followerCount: 0,
    followingCount: 0
  });
  const [watchlist, setWatchlist] = useState<string[]>([]);
  const [dailySummary, setDailySummary] = useState<string>('');
  const [isSDKLoaded, setIsSDKLoaded] = useState(false);


  const mockProjects: Project[] = [];

  const mockPosts: Post[] = [];


  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Initialize Farcaster SDK
        const context = await sdk.context;
        setIsSDKLoaded(true);

        if (context.user?.fid) {
          const userFid = context.user.fid;
          setUserStats(prev => ({
            ...prev,
            fid: userFid,
            username: context.user.username || `User ${userFid}`
          }));

          // Fetch user's watchlist and stats
          await Promise.all([
            fetchWatchlist(userFid),
            fetchUserStats(userFid),
            fetchDailySummary(userFid)
          ]);
        }

        sdk.actions.ready();
      } catch (error) {
        console.error('Failed to initialize Farcaster SDK:', error);
        // Continue with mock data
      }
    };

    initializeApp();
  }, []);

  // Fetch watchlist from Supabase
  const fetchWatchlist = async (userFid: number) => {
    try {
      const { data, error } = await supabase
        .from('watchlists')
        .select('project')
        .eq('fid', userFid)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) {
        setWatchlist(data.map((item: { project: string }) => item.project));
      }
    } catch (error) {
      console.error('Error fetching watchlist:', error);
      showToast('Failed to load watchlist', 'error');
    }
  };

  // Fetch user stats from API route
  const fetchUserStats = async (userFid: number) => {
    try {
      const response = await fetch(`/api/user-stats?fid=${userFid}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch user stats');
      }
      
      const stats = await response.json();
      
      setUserStats(prev => ({
        ...prev,
        followerCount: stats.followerCount,
        followingCount: stats.followingCount,
        username: stats.username || prev.username
      }));
    } catch (error) {
      console.error('Error fetching user stats:', error);
      showToast('Could not load follower stats', 'warning');
    }
  };

  // Fetch daily summary
  const fetchDailySummary = async (userFid: number) => {
    try {
      const { data, error } = await supabase
        .from('daily_summaries')
        .select('summary')
        .eq('fid', userFid)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      if (data) {
        setDailySummary(data.summary);
      }
    } catch (error) {
      console.error('Error fetching daily summary:', error);
    }
  };

  useEffect(() => {
    setIsHydrated(true);
    // Fetch real posts from watchlist projects
    const fetchPosts = async () => {
      if (watchlist.length === 0) {
        setIsLoading(false);
        return;
      }

      try {
        // TODO: Implement actual API call to fetch posts from tracked projects
        // For now, we'll show empty state
        setPosts([]);
      } catch (error) {
        console.error('Error fetching posts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (watchlist.length > 0) {
      fetchPosts();
    } else {
      setIsLoading(false);
    }
  }, [watchlist]);

  // Filter and sort posts
  useEffect(() => {
    if (!isHydrated) return;

    let filtered = [...posts];

    // Filter by project
    if (selectedProject !== 'all') {
      filtered = filtered.filter((post) => post.projectId === selectedProject);
    }

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter((post) => post.status === selectedStatus);
    }

    // Sort posts
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        case 'most-liked':
          return b.likes - a.likes;
        case 'most-shared':
          return b.shares - a.shares;
        case 'newest':
        default:
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      }
    });

    setFilteredPosts(filtered);
  }, [posts, selectedProject, selectedStatus, sortBy, isHydrated]);

  const showToast = useCallback((message: string, type: Toast['type']) => {
    setToast({ message, type, isVisible: true });
  }, []);

  const hideToast = useCallback(() => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  }, []);

  const handleLike = useCallback((postId: string) => {
    setPosts((prev) => prev.map((post) =>
      post.id === postId ? { ...post, likes: post.likes + 1 } : post
    ));
    showToast('Post liked!', 'success');
  }, [showToast]);

  const handleShare = useCallback((postId: string) => {
    setPosts((prev) => prev.map((post) =>
      post.id === postId ? { ...post, shares: post.shares + 1 } : post
    ));
    showToast('Post shared successfully!', 'success');
  }, [showToast]);

  const handleApprove = useCallback((postId: string) => {
    setPosts((prev) => prev.map((post) =>
      post.id === postId ? { ...post, status: 'approved' as const } : post
    ));
    showToast('Post approved for publishing!', 'success');
  }, [showToast]);

  const handleReject = useCallback((postId: string) => {
    setPosts((prev) => prev.map((post) =>
      post.id === postId ? { ...post, status: 'rejected' as const } : post
    ));
    showToast('Post rejected', 'warning');
  }, [showToast]);

  const handleRegenerate = useCallback((postId: string) => {
    showToast('Regenerating post with AI...', 'info');
    setTimeout(() => {
      showToast('New post generated successfully!', 'success');
    }, 2000);
  }, [showToast]);

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:277:6" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="277" data-component-end-line="283" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:278:8" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="278" data-component-end-line="282" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20py-6%20pb-20%22%7D" className="container mx-auto px-4 py-6 pb-20">
          <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:279:10" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="279" data-component-end-line="281" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
            {[...Array(3)].map((_, i) => <PostSkeleton data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:280:41" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="280" data-component-end-line="280" data-component-file="DashboardInteractive.tsx" data-component-name="PostSkeleton" data-component-content="%7B%22elementName%22%3A%22PostSkeleton%22%7D" key={i} />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:288:4" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="288" data-component-end-line="425" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:289:6" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="289" data-component-end-line="414" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-auto%20px-4%20py-6%20pb-20%20max-w-full%22%7D" className="mx-auto px-4 py-6 pb-20 max-w-full">
        {/* User Stats Header */}
        <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:291:8" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="291" data-component-end-line="309" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-xl%20p-4%20mb-6%20shadow-subtle%20border%20border-border%22%7D" className="bg-card rounded-xl p-4 mb-6 shadow-subtle border border-border">
          <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:292:10" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="292" data-component-end-line="308" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
            <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:293:12" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="293" data-component-end-line="303" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%22%7D" className="flex items-center gap-3">
              <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:294:14" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="294" data-component-end-line="296" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-12%20h-12%20bg-gradient-to-br%20from-primary%20to-secondary%20rounded-full%20flex%20items-center%20justify-center%20font-bold%20text-white%22%7D" className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center font-bold text-white">
                {userStats.username.charAt(0).toUpperCase()}
              </div>
              <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:297:14" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="297" data-component-end-line="302" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                <h2 data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:298:16" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="298" data-component-end-line="298" data-component-file="DashboardInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-semibold%20text-foreground%22%7D" className="font-semibold text-foreground">{userStats.username}</h2>
                <p data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:299:16" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="299" data-component-end-line="301" data-component-file="DashboardInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22followers%20%C2%B7%20following%22%7D" className="text-sm text-muted-foreground">
                  {userStats.followerCount} followers · {userStats.followingCount} following
                </p>
              </div>
            </div>
            <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:304:12" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="304" data-component-end-line="307" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-right%22%7D" className="text-right">
              <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:305:14" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="305" data-component-end-line="305" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22Tracking%22%7D" className="text-sm text-muted-foreground">Tracking</div>
              <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:306:14" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="306" data-component-end-line="306" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-2xl%20font-bold%20text-primary%22%7D" className="text-2xl font-bold text-primary">{watchlist.length}</div>
            </div>
          </div>
        </div>

        {/* Daily Summary */}
        {dailySummary && (
          <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:313:10" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="313" data-component-end-line="316" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-accent%2F10%20border%20border-accent%2F20%20rounded-xl%20p-4%20mb-6%22%7D" className="bg-accent/10 border border-accent/20 rounded-xl p-4 mb-6">
            <h3 data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:314:12" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="314" data-component-end-line="314" data-component-file="DashboardInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-sm%20font-semibold%20text-accent%20mb-2%22%2C%22textContent%22%3A%22%F0%9F%93%8A%20Today's%20Summary%22%7D" className="text-sm font-semibold text-accent mb-2">📊 Today's Summary</h3>
            <p data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:315:12" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="315" data-component-end-line="315" data-component-file="DashboardInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-foreground%22%7D" className="text-sm text-foreground">{dailySummary}</p>
          </div>
        )}

        {/* Watchlist Preview */}
        {watchlist.length > 0 && (
          <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:321:10" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="321" data-component-end-line="346" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-xl%20p-4%20mb-6%20shadow-subtle%20border%20border-border%22%7D" className="bg-card rounded-xl p-4 mb-6 shadow-subtle border border-border">
            <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:322:12" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="322" data-component-end-line="330" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-3%22%7D" className="flex items-center justify-between mb-3">
              <h3 data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:323:14" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="323" data-component-end-line="323" data-component-file="DashboardInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-semibold%20text-foreground%22%2C%22textContent%22%3A%22Your%20Watchlist%22%7D" className="font-semibold text-foreground">Your Watchlist</h3>
              <button data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:324:14" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="324" data-component-end-line="329" data-component-file="DashboardInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22text-sm%20text-primary%20hover%3Atext-primary%2F80%20transition%22%2C%22textContent%22%3A%22View%20All%20%E2%86%92%22%7D"
                onClick={() => router.push('/add-project')}
                className="text-sm text-primary hover:text-primary/80 transition"
              >
                View All →
              </button>
            </div>
            <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:331:12" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="331" data-component-end-line="345" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-2%22%7D" className="flex flex-wrap gap-2">
              {watchlist.slice(0, 5).map((project, idx) => (
                <span data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:333:16" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="333" data-component-end-line="338" data-component-file="DashboardInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22px-3%20py-1%20bg-muted%20rounded-full%20text-sm%20text-muted-foreground%22%7D"
                  key={idx}
                  className="px-3 py-1 bg-muted rounded-full text-sm text-muted-foreground"
                >
                  {project}
                </span>
              ))}
              {watchlist.length > 5 && (
                <span data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:341:16" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="341" data-component-end-line="343" data-component-file="DashboardInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22px-3%20py-1%20bg-muted%20rounded-full%20text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22%2B%20more%22%7D" className="px-3 py-1 bg-muted rounded-full text-sm text-muted-foreground">
                  +{watchlist.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Filters - Collapsible */}
        {showFilters && (
          <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:351:10" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="351" data-component-end-line="361" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%22%7D" className="mb-6">
            <FilterControls data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:352:12" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="352" data-component-end-line="360" data-component-file="DashboardInteractive.tsx" data-component-name="FilterControls" data-component-content="%7B%22elementName%22%3A%22FilterControls%22%7D"
              selectedProject={selectedProject}
              selectedStatus={selectedStatus}
              sortBy={sortBy}
              onProjectChange={setSelectedProject}
              onStatusChange={setSelectedStatus}
              onSortChange={setSortBy}
              projects={mockProjects}
            />
          </div>
        )}

        {/* Toggle Filters Button */}
        <button data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:365:8" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="365" data-component-end-line="373" data-component-file="DashboardInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22mb-4%20text-sm%20text-primary%20hover%3Atext-primary%2F80%20transition%20flex%20items-center%20gap-2%22%7D"
          onClick={() => setShowFilters(!showFilters)}
          className="mb-4 text-sm text-primary hover:text-primary/80 transition flex items-center gap-2"
        >
          <svg data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:369:10" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="369" data-component-end-line="371" data-component-file="DashboardInteractive.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-4%20h-4%22%7D" className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:370:12" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="370" data-component-end-line="370" data-component-file="DashboardInteractive.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>

        {isLoading ? (
          <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:376:10" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="376" data-component-end-line="378" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
            {[...Array(3)].map((_, i) => <PostSkeleton data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:377:41" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="377" data-component-end-line="377" data-component-file="DashboardInteractive.tsx" data-component-name="PostSkeleton" data-component-content="%7B%22elementName%22%3A%22PostSkeleton%22%7D" key={i} />)}
          </div>
        ) : filteredPosts.length === 0 ? (
          <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:380:10" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="380" data-component-end-line="398" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-xl%20p-8%20text-center%20border%20border-border%22%7D" className="bg-card rounded-xl p-8 text-center border border-border">
            <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:381:12" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="381" data-component-end-line="385" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-20%20h-20%20bg-muted%20rounded-full%20flex%20items-center%20justify-center%20mx-auto%20mb-4%22%7D" className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <svg data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:382:14" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="382" data-component-end-line="384" data-component-file="DashboardInteractive.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-10%20h-10%20text-muted-foreground%22%7D" className="w-10 h-10 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:383:16" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="383" data-component-end-line="383" data-component-file="DashboardInteractive.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
            <h3 data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:386:12" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="386" data-component-end-line="386" data-component-file="DashboardInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-xl%20font-bold%20text-foreground%20mb-2%22%2C%22textContent%22%3A%22No%20Posts%20Yet%22%7D" className="text-xl font-bold text-foreground mb-2">No Posts Yet</h3>
            <p data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:387:12" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="387" data-component-end-line="391" data-component-file="DashboardInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%20mb-6%22%7D" className="text-muted-foreground mb-6">
              {watchlist.length === 0 
                ? "Start tracking projects to see AI-generated posts here" 
                : "Posts from your tracked projects will appear here"}
            </p>
            <button data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:392:12" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="392" data-component-end-line="397" data-component-file="DashboardInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22bg-gradient-to-r%20from-primary%20to-secondary%20text-white%20px-6%20py-3%20rounded-lg%20font-semibold%20hover%3Aopacity-90%20transition%22%7D"
              onClick={() => router.push('/add-project')}
              className="bg-gradient-to-r from-primary to-secondary text-white px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition"
            >
              {watchlist.length === 0 ? 'Add Your First Project' : 'Add More Projects'}
            </button>
          </div>
        ) : (
          <div data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:400:10" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="400" data-component-end-line="412" data-component-file="DashboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
            {filteredPosts.map((post) => (
              <PostCard data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:402:14" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="402" data-component-end-line="410" data-component-file="DashboardInteractive.tsx" data-component-name="PostCard" data-component-content="%7B%22elementName%22%3A%22PostCard%22%7D"
                key={post.id}
                post={post}
                onLike={handleLike}
                onShare={handleShare}
                onApprove={handleApprove}
                onReject={handleReject}
                onRegenerate={handleRegenerate}
              />
            ))}
          </div>
        )}
      </div>

      <FloatingActionButton data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:416:6" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="416" data-component-end-line="416" data-component-file="DashboardInteractive.tsx" data-component-name="FloatingActionButton" data-component-content="%7B%22elementName%22%3A%22FloatingActionButton%22%7D" />
      <BottomTabNavigation data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:417:6" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="417" data-component-end-line="417" data-component-file="DashboardInteractive.tsx" data-component-name="BottomTabNavigation" data-component-content="%7B%22elementName%22%3A%22BottomTabNavigation%22%7D" />
      
      <ToastNotification data-component-id="src/app/home-dashboard/components/DashboardInteractive.tsx:419:6" data-component-path="src/app/home-dashboard/components/DashboardInteractive.tsx" data-component-line="419" data-component-end-line="424" data-component-file="DashboardInteractive.tsx" data-component-name="ToastNotification" data-component-content="%7B%22elementName%22%3A%22ToastNotification%22%7D"
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </div>
  );
};

export default DashboardInteractive;